<template>
  <div class="hcl-form-element-multi-select">
    <Multiselect mode="tags" class="hcl-form-element-multi-select-select" :class="{
      'is-valid': validationStatus === 'valid',
      'is-invalid': validationStatus === 'invalid',
      'ui-disabled': isReadOnly(props.definitionField.field)
    }" ref="fieldRef" v-model="props.dataField.value" :disabled="isReadOnly(props.definitionField.field)"
      :required="props.definitionField.field.required" :hidden="props.definitionField.field.hidden" label="text"
      :placeholder="fieldPlaceholder" :options="fieldLookup" :searchable="true" :canClear="false"
      :close-on-select="!isCtrlPressed" @keydown="handleKeyPress" @keyup="handleKeyPress"
    >
      <template #nooptions>
        {{ fieldPlaceholderNoOptions }}
      </template>
    </Multiselect>
    <input v-for="key in props.dataField.value" :key="key" type="hidden" :name="fieldName + '[]'" :value="key"
      :widget="props.definitionField.field.widget?.widget" v-bind="generateDataAttributes" />
    <template v-if="!isReadOnly(props.definitionField.field)">
      <span class="select-all-button" type="button" @click="selectAllOptions">
        <i class="eii--fa6-solid--circle-check"></i>
      </span>
      <span class="deselect-all-button" type="button" @click="deselectAllOptions">
        <i class="eii--fa6-solid--circle-xmark"></i>
      </span>
    </template>
  </div>
</template>

<script setup lang="ts">
import { ref, onMounted } from 'vue'

import { useElement } from '@/components/FormElement/element'

import Multiselect from '@vueform/multiselect'

interface Props {
  definitionField: any
  dataField: any
  dataLookup: any
  dropdownmode?: any
}

const props = withDefaults(defineProps<Props>(), {
  definitionField: undefined,
  dataField: undefined,
  dataLookup: undefined,
  dropdownmode: undefined
})

const {
  fieldRef,
  isReadOnly,
  fieldName,
  fieldLookup,
  fieldPlaceholder,
  fieldPlaceholderNoOptions,
  validationStatus,
  generateDataAttributes
} = useElement(props)

onMounted(() => {
  if (props.definitionField.field.focus) fieldRef.value.$refs.multiselect.focus()
})

const isCtrlPressed = ref(false)

const handleKeyPress = (event: Event, instance: Multiselect) => {
  const keyboardEvent = event as KeyboardEvent
  isCtrlPressed.value = keyboardEvent.ctrlKey
}

const selectAllOptions = () => {
  fieldRef.value.selectAll()
}

const deselectAllOptions = () => {
  fieldRef.value.clear()
}
</script>

<style lang="scss">
.hcl-form-element-multi-select {
  width: 100%;
  position: relative;

  .select-all-button,
  .deselect-all-button {
    color: rgb(208, 208, 208);
    background-color: white;
    position: absolute;
    top: calc(50% - 12px);

    i {
      height: 1.2em;
      width: 1.2em;
    }

    &:hover {
      color: rgb(153, 153, 153);
    }
  }

  .select-all-button {
    right: 3.8rem;
  }

  .deselect-all-button {
    right: 2.2rem;
  }

  .multiselect {
    --ms-py: 5px;
    --ms-px: 0.875rem;
    --ms-max-height: 15rem;

    .multiselect-wrapper {
      justify-content: space-between;

      .multiselect-placeholder,
      .multiselect-single-label {
        white-space: nowrap;
        overflow: hidden;
      }

      .multiselect-placeholder {
        right: 2rem;
      }

      .multiselect-single-label {
        right: 2rem;
        padding-right: var(--ms-px, .875rem);
      }
    }

    &.is-active {
      box-shadow: inset 0 1px 1px rgba(0, 0, 0, 0.075), 0 0 0 0.1875rem hsla(var(--theme-color-interaction-h), var(--theme-color-interaction-s), var(--theme-color-interaction-l), 0.15);
      border-color: var(--theme-color-interaction);
    }

    .multiselect-option {
      &.is-selected {
        background-color: var(--theme-color-interaction);
      }
    }

    .multiselect-tags {
      max-width: calc(100% - 4rem - var(--ms-px));

      .multiselect-tag {
        background-color: var(--theme-color-interaction);
        white-space: break-spaces;
      }
    }
  }
}
</style>
